/** Notification data hooks — spec §5-F14, §7.7. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '@/lib/api';

export interface NotificationView {
  _id: string;
  type: string;
  title: string;
  body?: string;
  entityType?: string;
  entityId?: string;
  entityKey?: string;
  projectId?: string;
  url?: string;
  isRead: boolean;
  groupKey?: string;
  groupCount?: number;
  snoozedUntil?: string;
  createdAt: string;
}

export function useNotifications(filters: { unreadOnly?: boolean } = {}) {
  return useQuery({
    queryKey: ['notifications', filters],
    queryFn: async () =>
      (await api.get<NotificationView[]>(`/notifications${qs({ ...filters, limit: 50 })}`)).data,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['unread-count'],
    queryFn: async () => (await api.get<{ count: number }>('/notifications/unread-count')).data.count,
    // The socket pushes the real updates; this is the safety net for a tab that
    // was asleep or missed a reconnect.
    refetchInterval: 120_000,
  });
}

function useNotificationInvalidation() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    void qc.invalidateQueries({ queryKey: ['unread-count'] });
  };
}

export function useMarkRead() {
  const invalidate = useNotificationInvalidation();
  return useMutation({
    mutationFn: async (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: invalidate,
  });
}

export function useMarkAllRead() {
  const invalidate = useNotificationInvalidation();
  return useMutation({
    mutationFn: async () => api.post('/notifications/read-all'),
    onSuccess: invalidate,
  });
}

export function useSnooze() {
  const invalidate = useNotificationInvalidation();
  return useMutation({
    mutationFn: async ({ id, hours }: { id: string; hours: number }) =>
      api.post(`/notifications/${id}/snooze`, {
        until: new Date(Date.now() + hours * 3_600_000).toISOString(),
      }),
    onSuccess: invalidate,
  });
}
